import axios from 'axios';
import { gcApi, ghsApi } from 'config/axios';
import { takeEvery, call, put } from 'redux-saga/effects';

import { toQueryString } from 'utils/UrlUtil';
import DateFormatter from 'utils/date/DateFormatter';
import { listOptions, ecgPreferenceKey } from 'utils/ecg/ecgConstants';
import { getUpdatedFilters, getResultTypesForQueryParams } from 'utils/ecg/ecgUtil';

import { shouldRestrictByRole } from 'utils/WindowPreferencesMappings';
import { ecgRoleKey } from 'backbone/models/preferences/PreferencesMapping';
import ViewerSocialProfile from 'models/user/ViewerSocialProfile';

import {
    FETCH_ECG_RECORDING_LIST,
    FETCH_ECG_RECORDING_LIST_START,
    FETCH_ECG_RECORDING_LIST_SUCCESS,
    FETCH_ECG_RECORDING_LIST_FAIL,
    FETCH_ECG_RECORDING_DETAILS,
    FETCH_ECG_RECORDING_DETAILS_START,
    FETCH_ECG_RECORDING_DETAILS_SUCCESS,
    FETCH_ECG_RECORDING_DETAILS_FAIL,
    FETCH_ECG_RELATIVE_RECORDINGS,
    FETCH_ECG_RELATIVE_RECORDINGS_START,
    FETCH_ECG_RELATIVE_RECORDINGS_SUCCESS,
    FETCH_ECG_RELATIVE_RECORDINGS_FAIL,
    FETCH_MORE_ECG_RECORDING_LIST,
    FETCH_MORE_ECG_RECORDING_LIST_SUCCESS,
    DELETE_ECG_RECORDING,
    DELETE_ECG_RECORDING_START,
    DELETE_ECG_RECORDING_SUCCESS,
    DELETE_ECG_RECORDING_FAIL,
    FETCH_ECG_RECORDINGS_FOR_CALENDAR,
    FETCH_ECG_RECORDINGS_FOR_CALENDAR_START,
    FETCH_ECG_RECORDINGS_FOR_CALENDAR_SUCCESS,
    FETCH_ECG_RECORDINGS_FOR_CALENDAR_FAIL,
    FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY,
    FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY_START,
    FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY_SUCCESS,
    FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY_FAIL,
    UPDATE_ECG_LIST_FILTERS,
    RESET_ECG_LIST_FILTERS,
    FETCH_ECG_VIDEO_TUTORIAL,
    FETCH_ECG_VIDEO_TUTORIAL_START,
    FETCH_ECG_VIDEO_TUTORIAL_SUCCESS,
    FETCH_ECG_VIDEO_TUTORIAL_FAIL
} from '../actions/ActionTypes';

const getQueryParamsForEcg = (updatedFilters) => {
    const queryParams = {};

    if (updatedFilters.symptoms && updatedFilters.symptoms.length > 0) {
        queryParams.symptom = updatedFilters.symptoms;
    }

    if (updatedFilters.resultTypes && updatedFilters.resultTypes.length > 0) {
        queryParams.result = getResultTypesForQueryParams(updatedFilters.resultTypes);
    }

    const { fromDate, toDate } = updatedFilters.dates;
    queryParams.firstDay = fromDate ? DateFormatter.formatISODate(fromDate) : listOptions.startTimeOfEcg;
    queryParams.lastDay = toDate ? DateFormatter.formatISODate(toDate) : DateFormatter.formatISODate(new Date());

    queryParams.pageSize = listOptions.pageSize;

    // needed for fetching the next page
    if (updatedFilters.page) {
        queryParams.page = updatedFilters.page;
    }

    return toQueryString(queryParams);
};

export function* fetchEcgRecordingList(action) {
    try {
        yield put({
            type: FETCH_ECG_RECORDING_LIST_START,
            loadingType: action.loadingType || listOptions.loadingTypes.fetchInitial
        });

        const { activeFilters, filterType, filterValue } = action && action.payload ? action.payload : {};
        const updatedFilters = getUpdatedFilters(activeFilters, filterType, filterValue);
        const queryParams = getQueryParamsForEcg(updatedFilters);

        const response = yield call(ghsApi.get, `/ghs/ecg/v1/ecg/list${queryParams}`);

        yield put({
            type: FETCH_ECG_RECORDING_LIST_SUCCESS,
            data: response.data || []
        });
    } catch (e) {
        yield put({
            type: FETCH_ECG_RECORDING_LIST_FAIL,
            data: e.response && e.response.data
        });
    }
}

export function* fetchMoreEcgRecordingList(action) {
    try {
        yield put({
            type: FETCH_ECG_RECORDING_LIST_START,
            loadingType: action.payload.loadingType ? action.payload.loadingType : listOptions.loadingTypes.fetchMore
        });

        const { activeFilters, nextToken } = action && action.payload ? action.payload : {};
        const updatedFilters = {
            ...activeFilters,
            page: nextToken
        };
        const queryParams = getQueryParamsForEcg(updatedFilters);

        const response = yield call(ghsApi.get, `/ghs/ecg/v1/ecg/list/next${queryParams}`);

        yield put({
            type: FETCH_MORE_ECG_RECORDING_LIST_SUCCESS,
            data: response.data || []
        });
    } catch (e) {
        yield put({
            type: FETCH_ECG_RECORDING_LIST_FAIL,
            data: e.response && e.response.data
        });
    }
}

export function* fetchEcgRecordingDetails(action) {
    try {
        yield put({
            type: FETCH_ECG_RECORDING_DETAILS_START
        });

        const { ecgId } = action.payload;
        const response = yield call(ghsApi.get, `ghs/ecg/v1/ecg/${ecgId}`);

        yield put({
            type: FETCH_ECG_RECORDING_DETAILS_SUCCESS,
            data: response.data || {}
        });

        if (
            response.data &&
            response.data.summary &&
            response.data.summary.detailId &&
            response.data.summary.startTime
        ) {
            yield call(fetchEcgRelativeRecordings, {
                payload: {
                    detailId: response.data.summary.detailId,
                    startTime: response.data.summary.startTime
                }
            });
        }
    } catch (e) {
        yield put({
            type: FETCH_ECG_RECORDING_DETAILS_FAIL,
            data: e.response && e.response.data
        });
    }
}

export function* fetchEcgRelativeRecordings(action) {
    try {
        yield put({
            type: FETCH_ECG_RELATIVE_RECORDINGS_START
        });

        const { startTime, detailId } = action.payload;
        const response = yield call(ghsApi.get, `ghs/ecg/v1/ecg/relative/${startTime}`);

        const { data } = response;

        yield put({
            type: FETCH_ECG_RELATIVE_RECORDINGS_SUCCESS,
            data: {
                relativeRecordings: data || {},
                detailId
            }
        });
    } catch (e) {
        yield put({
            type: FETCH_ECG_RELATIVE_RECORDINGS_FAIL
        });
    }
}

export function* deleteEcgRecording(action) {
    try {
        const { ecgId } = action.payload;

        yield put({
            type: DELETE_ECG_RECORDING_START,
            payload: ecgId
        });

        yield call(ghsApi.delete, `/ghs/ecg/v1/ecg/${ecgId}`);

        yield put({
            type: DELETE_ECG_RECORDING_SUCCESS,
            payload: ecgId
        });
    } catch (e) {
        yield put({
            type: DELETE_ECG_RECORDING_FAIL
        });
    }
}

export function* fetchEcgRecordingsForCalendar(action) {
    try {
        if (shouldRestrictByRole(ecgRoleKey, ViewerSocialProfile.get('userRoles'))) {
            return;
        }

        const { firstDay, lastDay, periodType } = action.payload;

        yield put({
            type: FETCH_ECG_RECORDINGS_FOR_CALENDAR_START,
            periodType
        });

        const response = yield call(ghsApi.get, `/ghs/ecg/v1/ecg/daily?firstDay=${firstDay}&lastDay=${lastDay}`);
        yield put({
            type: FETCH_ECG_RECORDINGS_FOR_CALENDAR_SUCCESS,
            data: response.data && response.data.content ? response.data.content : [],
            periodType
        });
    } catch (e) {
        const { periodType } = action.payload;
        yield put({
            type: FETCH_ECG_RECORDINGS_FOR_CALENDAR_FAIL,
            periodType
        });
    }
}

export function* fetchEcgRecordingsForDailySummary(action) {
    try {
        if (shouldRestrictByRole(ecgRoleKey, ViewerSocialProfile.get('userRoles'))) {
            return;
        }

        const preferenceResponse = yield call(gcApi.get, `/userpreference-service/${ecgPreferenceKey}`);
        if (preferenceResponse.data.value !== 'true') {
            return;
        }

        yield put({
            type: FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY_START
        });

        const calendarDate = action.payload;
        const { firstDay, lastDay } = action.payload;

        const first = firstDay || calendarDate;
        const last = lastDay || calendarDate;

        const response = yield call(ghsApi.get, `/ghs/ecg/v1/ecg/daily?firstDay=${first}&lastDay=${last}`);
        yield put({
            type: FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY_SUCCESS,
            data: response.data || [],
            calendarDate: calendarDate
        });
    } catch (e) {
        yield put({
            type: FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY_FAIL
        });
    }
}

export function* fetchEcgVideoTutorial(action) {
    try {
        yield put({
            type: FETCH_ECG_VIDEO_TUTORIAL_START
        });

        const { skuNumber } = action.payload;

        const response = yield call(axios.post, `/asset-service/download-manifest/`, {
            skuNumber: skuNumber,
            appType: 'GCM',
            resolution: 720
        });

        yield put({
            type: FETCH_ECG_VIDEO_TUTORIAL_SUCCESS,
            data: response.data || {}
        });
    } catch (e) {
        yield put({
            type: FETCH_ECG_VIDEO_TUTORIAL_FAIL,
            data: e.response && e.response.data
        });
    }
}

export const ecgRecordingSagas = [
    takeEvery(FETCH_ECG_RECORDING_LIST, fetchEcgRecordingList),
    takeEvery(UPDATE_ECG_LIST_FILTERS, fetchEcgRecordingList),
    takeEvery(RESET_ECG_LIST_FILTERS, fetchEcgRecordingList),
    takeEvery(FETCH_ECG_RECORDING_DETAILS, fetchEcgRecordingDetails),
    takeEvery(FETCH_ECG_RELATIVE_RECORDINGS, fetchEcgRelativeRecordings),
    takeEvery(FETCH_MORE_ECG_RECORDING_LIST, fetchMoreEcgRecordingList),
    takeEvery(DELETE_ECG_RECORDING, deleteEcgRecording),
    takeEvery(FETCH_ECG_RECORDINGS_FOR_CALENDAR, fetchEcgRecordingsForCalendar),
    takeEvery(FETCH_ECG_RECORDINGS_FOR_DAILY_SUMMARY, fetchEcgRecordingsForDailySummary),
    takeEvery(FETCH_ECG_VIDEO_TUTORIAL, fetchEcgVideoTutorial)
];
